import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Typography,
} from "@material-ui/core";
import React, { useEffect, useState } from "react";
import { TEXT } from "Clients/ACCB/ACCBTheme";
import { getMyPayments, initiateK2Payment, initiatePartyPayment } from "../../service";

const STATUS = {
  paid: { label: "Paid", bg: "#E4F5EA", color: TEXT.success },
  pending: { label: "Pending", bg: "#FDF2CF", color: TEXT.warning },
  initiated: { label: "In progress", bg: "#FDF2CF", color: TEXT.warning },
  failed: { label: "Failed", bg: "#FDE7E7", color: TEXT.error },
  cancelled: { label: "Cancelled", bg: "#FDE7E7", color: TEXT.error },
};

const CHECKOUT_BASE = {
  test: "https://testpay.easebuzz.in/pay/",
  prod: "https://pay.easebuzz.in/pay/",
};

// Khajane-II's payment page expects a browser form POST (not a redirect or an
// XHR), so build a hidden form with the fields the backend returned and submit it.
function submitToK2({ payment_url, method, fields }) {
  const form = document.createElement("form");
  form.method = method || "POST";
  form.action = payment_url;
  Object.entries(fields || {}).forEach(([name, value]) => {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = name;
    input.value = value;
    form.appendChild(input);
  });
  document.body.appendChild(form);
  form.submit();
}

export default function PartyPendingPayments({ disputeId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(null); // "<gateway>-<share_id>" while starting
  const [error, setError] = useState("");

  const load = () => {
    setLoading(true);
    getMyPayments({ dispute_id: disputeId })
      .then((res) => setItems(Array.isArray(res.data) ? res.data : []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };

  useEffect(load, [disputeId]);

  const payNow = (share) => {
    setPaying(`easebuzz-${share.share_id}`);
    setError("");
    initiatePartyPayment(share.share_id)
      .then((res) => {
        const { access_key, env } = res.data || {};
        if (!access_key) {
          setError("Could not start the payment. Please try again.");
          return;
        }
        const base = CHECKOUT_BASE[env] || CHECKOUT_BASE.test;
        window.location.href = `${base}${encodeURIComponent(access_key)}`;
      })
      .catch((err) => {
        setError(err?.response?.data?.error || "Payment could not be started.");
      })
      .finally(() => setPaying(null));
  };

  const payViaK2 = (share) => {
    setPaying(`k2-${share.share_id}`);
    setError("");
    initiateK2Payment(share.share_id)
      .then((res) => {
        const data = res.data || {};
        if (!data.payment_url || !data.fields) {
          setError("Could not start the Khajane-II payment. Please try again.");
          setPaying(null);
          return;
        }
        submitToK2(data); // leaves the page; keep the button disabled meanwhile
      })
      .catch((err) => {
        setError(err?.response?.data?.error || "Khajane-II payment could not be started.");
        setPaying(null);
      });
  };

  if (loading) {
    return (
      <Box display="flex" alignItems="center" style={{ gap: "0.5rem" }}>
        <CircularProgress size={16} />
        <Typography style={{ color: TEXT.muted, fontSize: "0.85rem" }}>
          Loading payments…
        </Typography>
      </Box>
    );
  }

  if (items.length === 0) return null; // nothing to show

  return (
    <Paper style={{ padding: "1.25rem", border: "1px solid #E5E5E5" }}>
      <Typography style={{ fontWeight: 600, fontSize: "1.1rem", marginBottom: "0.75rem" }}>
        Pending Payments
      </Typography>

      {error && (
        <Typography style={{ color: TEXT.error, fontSize: "0.85rem", marginBottom: "0.5rem" }}>
          {error}
        </Typography>
      )}

      {items.map((s) => {
        const st = STATUS[s.payment_status] || STATUS.pending;
        const canPay = s.payment_status !== "paid";
        return (
          <Box key={s.share_id}
               style={{
                 display: "flex", alignItems: "center", justifyContent: "space-between",
                 gap: "1rem", padding: "0.6rem 0", borderBottom: "1px solid #F0F0F0",
               }}>
            <Box>
              <Typography style={{ fontSize: "0.9rem", fontWeight: 600 }}>
                {s.case_number} — ₹{s.amount}
              </Typography>
              {s.is_hearing_room_fee && (
                <Typography style={{ fontSize: "0.8rem", color: TEXT.brand, fontWeight: 500 }}>
                  Payment for booking hearing slots
                </Typography>
              )}
              <Typography style={{ fontSize: "0.75rem", color: TEXT.muted }}>
                Challan {s.challan_number}
                {s.paid_at ? ` · Paid ${s.paid_at}` : ""}
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" style={{ gap: "0.75rem" }}>
              <Chip label={st.label} size="small"
                    style={{ backgroundColor: st.bg, color: st.color, fontWeight: 600 }} />
              {/* Hearing-slot fees are paid through Khajane-II only. */}
              {canPay && !s.is_hearing_room_fee && (
                <Button variant="contained" color="primary" size="small"
                        disabled={paying !== null}
                        onClick={() => payNow(s)}
                        style={{ textTransform: "none" }}>
                  {paying === `easebuzz-${s.share_id}` ? <CircularProgress size={16} /> : "Pay via Easebuzz"}
                </Button>
              )}
              {canPay && s.k2_available && (
                <Button variant="outlined" color="primary" size="small"
                        disabled={paying !== null}
                        onClick={() => payViaK2(s)}
                        style={{ textTransform: "none" }}>
                  {paying === `k2-${s.share_id}` ? <CircularProgress size={16} /> : "Pay via Khajane-II"}
                </Button>
              )}
            </Box>
          </Box>
        );
      })}
    </Paper>
  );
}
